import { isAppViewSpec, snapshotFeedState } from '@erp/json-renderer/apps';
import type { AppViewSpec } from '@erp/json-renderer/apps';
import { z } from 'zod';

// Only what rendering reads; the backend validated the whole manifest
// (`show_view` → AppManifestSchema, chat profile) before the call counts.
const showViewArgsSchema = z.object({
  title: z.string().optional(),
  manifest: z.object({
    feeds: z
      .record(
        z.string(),
        z.object({ kind: z.literal('snapshot'), value: z.unknown() }),
      )
      .default({}),
    view: z.custom<AppViewSpec>(isAppViewSpec),
    commands: z
      .record(
        z.string(),
        z.object({
          tool: z.string(),
          input: z.record(z.string(), z.unknown()).default({}),
        }),
      )
      .default({}),
  }),
});

const showViewResultSchema = z.object({ ok: z.literal(true) });

export interface ShowView {
  readonly title?: string;
  readonly view: AppViewSpec;
  /** The view's opening state: its own, then each feed's value and status. */
  readonly state: Record<string, unknown>;
  /** What the view's buttons run: an action tool and its input, by command. */
  readonly commands: Readonly<Record<string, ViewCommand>>;
}

export interface ViewCommand {
  readonly tool: string;
  readonly input: Record<string, unknown>;
}

/**
 * The view a `show_view` call shows, or null when there's nothing to show:
 * the call failed validation (the assistant fixes it and calls again) or its
 * args aren't a view. Snapshot feeds land as an App's feeds do: the value at
 * `/<key>`, a settled status at `/$data/<key>`.
 */
export function showViewFrom(
  args: Record<string, unknown>,
  result: unknown,
): ShowView | null {
  if (!showViewResultSchema.safeParse(result).success) return null;
  const parsed = showViewArgsSchema.safeParse(args);
  if (!parsed.success) return null;
  const { title, manifest } = parsed.data;
  return {
    title,
    view: manifest.view,
    commands: manifest.commands,
    state: snapshotFeedState(manifest.view.state, manifest.feeds),
  };
}
